import { yupResolver } from '@hookform/resolvers/yup'
import * as React from 'react'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { createContract, updateContract } from '../../../api/admin/contract'
import { FORMAT } from '../../../constants/format'
import { MODALS } from '../../../constants/modals'
import { useDispatch, useSelector } from '../../../store/hooks'
import { modalInitialState, modalSlice } from '../../../store/slices/modal'
import { formatDate, formatDateWithIncomingFormat } from '../../../utils/format'
import { BaseButton } from '../../atoms/BaseButton'
import { BaseLabel } from '../../atoms/BaseLabel'
import Flex from '../../atoms/Flex'
import { FieldBaseCounterInput } from '../../molecules/FieldBaseCounterInput'
import { FieldBaseInput } from '../../molecules/FieldBaseInput'
import { FieldDatePicker } from '../../molecules/FieldDatePicker'
import { Modal } from '../../molecules/Modal'

const schema = yup
  .object({
    cislo: yup.string().required(),
    popis: yup.string(),
    fixMo: yup.number().moreThan(-1),
    fixVo: yup.number().moreThan(-1),
    platnostOd: yup.string().required(),
    platnostDo: yup.string().nullable(true),
  })
  .required()

type Props = {
  onClose: () => void
}

export const CustomerContractDialog: React.FC<Props> = ({ onClose }) => {
  const { modal } = useSelector((state) => state)
  const isOpened = modal.customerContract
  const initialFormState = modal.customerContractCreatedOrChangeInitialState
  const initialFormStateEdited = {
    ...initialFormState,
    platnostOd:
      initialFormState.platnostOd &&
      formatDateWithIncomingFormat(
        initialFormState.platnostOd,
        FORMAT.API_DDMMYYYY,
        FORMAT.YYYYMMDD
      ),
    platnostDo:
      initialFormState.platnostDo &&
      formatDateWithIncomingFormat(
        initialFormState.platnostDo,
        FORMAT.API_DDMMYYYY,
        FORMAT.YYYYMMDD
      ),
  }

  const isContractUpdate = initialFormStateEdited.cislo.length > 0

  useEffect(() => {
    reset(initialFormStateEdited)
  }, [initialFormState])

  const { control, handleSubmit, setValue, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: useMemo(() => initialFormStateEdited, [initialFormStateEdited]),
  })

  const dispatch = useDispatch()
  const handleClose = () => {
    dispatch(
      modalSlice.actions.update({
        [MODALS.CUSTOMER_CONTRACT_CREATER_OR_CHANGE_STATE_MODAL]:
          modalInitialState.customerContractCreatedOrChangeInitialState,
        [MODALS.CUSTOMER_CONTRACT_MODAL]: false,
      })
    )
    reset()
  }

  const onSubmit = handleSubmit((data) => {
    if (data.platnostDo === null || data.platnostDo === 'Invalid Date') {
      delete data.platnostDo
    } else {
      data.platnostDo = formatDate(data.platnostDo, FORMAT.API_DDMMYYYY)
    }
    data.platnostOd = formatDate(data.platnostOd, FORMAT.API_DDMMYYYY)

    if (isContractUpdate) {
      updateContract(data)
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id: _id, ...dataWithoutId } = data
      createContract({ ...dataWithoutId })
    }
    handleClose()
    onClose()
  })

  return (
    <Modal
      type='main'
      open={isOpened}
      title={isContractUpdate ? 'Upravit smlouvu' : 'Nová smlouva'}
      modalId={MODALS.CUSTOMER_CREATER_OR_CHANGE_MODAL}
      onClose={() => handleClose()}
      centered>
      <form onSubmit={onSubmit} className='w-full'>
        <div className='grid gap-5 grid-cols-2'>
          <div className='col-span-2'>
            <BaseLabel>Číslo smlouvy</BaseLabel>
            <FieldBaseInput className='w-full' name='cislo' control={control} hiddenLabel />
          </div>
          <div className='col-span-2'>
            <BaseLabel>Textový popis</BaseLabel>
            <FieldBaseInput
              className='w-full'
              name='popis'
              multiline
              control={control}
              hiddenLabel
            />
          </div>
          <div className='col-span-1'>
            <BaseLabel>Fixní nároky na MZO</BaseLabel>
            <FieldBaseCounterInput
              name='fixMo'
              control={control}
              label='Fixní nároky na MZO'
              handleOnClickCounterButtons={(action, value) => {
                if (value < 0) return
                if (action === 'increase') {
                  setValue('fixMo', value + 1)
                }
                if (action === 'decrease') {
                  setValue('fixMo', value - 1)
                }
              }}
            />
          </div>
          <div className='col-span-1'>
            <BaseLabel>Fixní nároky na VZO</BaseLabel>
            <FieldBaseCounterInput
              name='fixVo'
              control={control}
              label='Fixní nároky na VZO'
              handleOnClickCounterButtons={(action, value) => {
                if (value < 0) return
                if (action === 'increase') {
                  setValue('fixVo', value + 1)
                }
                if (action === 'decrease') {
                  setValue('fixVo', value - 1)
                }
              }}
            />
          </div>
          <div className='col-span-1'>
            <BaseLabel>Platnost od</BaseLabel>
            <FieldDatePicker name='platnostOd' hiddenLabel control={control} />
          </div>
          <div className='col-span-1'>
            <BaseLabel>Platnost do</BaseLabel>
            <FieldDatePicker name='platnostDo' hiddenLabel control={control} />
          </div>
          <Flex justifyContent='end' className='col-span-2'>
            <BaseButton type='submit' variant='contained' color='primary' size='large'>
              {isContractUpdate ? 'Uložit změny' : 'Vytvořit smlouvu'}
            </BaseButton>
          </Flex>
        </div>
      </form>
    </Modal>
  )
}
